import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { MessageSquare, Plus, RefreshCw, Search, Settings, Trash2 } from 'lucide-react';
import { useWhatsAppAccounts, useCreateWhatsAppAccount, useDeleteWhatsAppAccount, useGenerateAuthUrl, useStoreWhatsAppAccount, useRegisterPhoneNumber, useSubscribeApp, usePhoneNumbers, useUpdateWebhook } from '../hooks/useApi';
import { useToast } from '../components/ui/toaster';
import FacebookSignupModal from '../components/FacebookSignupModal';
import type { WhatsAppAccount, CreateWhatsAppRequest, PhoneNumber } from '../services/api';

export default function Accounts() {
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showFacebookSignup, setShowFacebookSignup] = useState(false);
    const [showPinDialog, setShowPinDialog] = useState(false);
    const [pinValue, setPinValue] = useState('');
    const [accountToRegister, setAccountToRegister] = useState<string | null>(null);
    const [showWebhookDialog, setShowWebhookDialog] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState('');
    const [webhookEnabled, setWebhookEnabled] = useState(false);
    const [phoneNumberToConfigure, setPhoneNumberToConfigure] = useState<PhoneNumber | null>(null);

    const { data: accounts, isLoading, error, refetch } = useWhatsAppAccounts();
    const { data: phoneNumbers } = usePhoneNumbers();
    const createAccountMutation = useCreateWhatsAppAccount();
    const deleteAccountMutation = useDeleteWhatsAppAccount();
    const registerPhoneNumberMutation = useRegisterPhoneNumber();
    const subscribeAppMutation = useSubscribeApp();
    const updateWebhookMutation = useUpdateWebhook();
    const { toast } = useToast();

    const filteredAccounts = accounts?.filter(account =>
        account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.channel.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const handleRefresh = () => {
        refetch();
        toast({
            title: 'Atualizado',
            description: 'Lista de contas atualizada',
            variant: 'success',
        });
    };

    const handleCreateAccount = async (data: CreateWhatsAppRequest) => {
        try {
            await createAccountMutation.mutateAsync(data);
            toast({
                title: 'Sucesso',
                description: 'Conta WhatsApp criada com sucesso!',
                variant: 'success',
            });
            setShowCreateForm(false);
        } catch (error) {
            toast({
                title: 'Erro',
                description: 'Falha ao criar conta WhatsApp',
                variant: 'destructive',
            });
        }
    };

    const handleDeleteAccount = async (id: string) => {
        if (confirm('Tem certeza que deseja deletar esta conta?')) {
            try {
                await deleteAccountMutation.mutateAsync(id);
                toast({
                    title: 'Sucesso',
                    description: 'Conta deletada com sucesso!',
                    variant: 'success',
                });
            } catch (error) {
                toast({
                    title: 'Erro',
                    description: 'Falha ao deletar conta',
                    variant: 'destructive',
                });
            }
        }
    };

    const handleFacebookSignupSuccess = () => {
        toast({
            title: 'Sucesso',
            description: 'Conta WhatsApp conectada com sucesso!',
            variant: 'success',
        });
    };

    const handleRegisterPhoneNumber = (accountId: string) => {
        setAccountToRegister(accountId);
        setPinValue('');
        setShowPinDialog(true);
    };

    const handlePinSubmit = async () => {
        if (!accountToRegister) return;

        if (!pinValue || pinValue.length !== 6 || !/^\d{6}$/.test(pinValue)) {
            toast({
                title: 'Erro',
                description: 'PIN deve ter exatamente 6 dígitos numéricos',
                variant: 'destructive',
            });
            return;
        }

        try {
            await registerPhoneNumberMutation.mutateAsync({ id: accountToRegister, pin: pinValue });
            toast({
                title: 'Sucesso',
                description: 'Número registrado com sucesso!',
                variant: 'success',
            });
            setShowPinDialog(false);
            setPinValue('');
            setAccountToRegister(null);
        } catch (error) {
            toast({
                title: 'Erro',
                description: 'Falha ao registrar número',
                variant: 'destructive',
            });
        }
    };

    const handleSubscribeApp = async (accountId: string) => {
        try {
            await subscribeAppMutation.mutateAsync(accountId);
            toast({
                title: 'Sucesso',
                description: 'App conectado com sucesso!',
                variant: 'success',
            });
        } catch (error) {
            toast({
                title: 'Erro',
                description: 'Falha ao conectar app',
                variant: 'destructive',
            });
        }
    };

    const getPhoneNumberStatus = (accountId: string) => {
        const phoneNumber = phoneNumbers?.find(pn => pn.whatsappAccountId === accountId);
        return {
            isRegistered: phoneNumber?.isRegistered || false,
            isSubscribed: phoneNumber?.isSubscribed || false,
        };
    };

    const handleOpenWebhookConfig = (account: WhatsAppAccount) => {
        // Find phone number associated with this account
        const phoneNumber = phoneNumbers?.find(pn => pn.whatsappAccountId === account.publicId);
        if (!phoneNumber) {
            toast({
                title: 'Erro',
                description: 'Número de telefone não encontrado para esta conta',
                variant: 'destructive',
            });
            return;
        }
        setPhoneNumberToConfigure(phoneNumber);
        const hasWebhook = !!phoneNumber.webhookUrl && phoneNumber.webhookUrl.trim() !== '';
        setWebhookEnabled(hasWebhook);
        setWebhookUrl(phoneNumber.webhookUrl || '');
        setShowWebhookDialog(true);
    };

    const handleWebhookSubmit = async () => {
        if (!phoneNumberToConfigure) return;

        // If webhook is enabled, validate URL
        if (webhookEnabled) {
            if (!webhookUrl.trim()) {
                toast({
                    title: 'Erro',
                    description: 'URL do webhook é obrigatória quando o webhook está habilitado',
                    variant: 'destructive',
                });
                return;
            }

            try {
                new URL(webhookUrl);
            } catch {
                toast({
                    title: 'Erro',
                    description: 'URL do webhook inválida',
                    variant: 'destructive',
                });
                return;
            }
        }

        try {
            await updateWebhookMutation.mutateAsync({
                id: phoneNumberToConfigure.publicId,
                webhookUrl: webhookEnabled ? webhookUrl.trim() : '',
            });
            toast({
                title: 'Sucesso',
                description: webhookEnabled
                    ? 'Webhook habilitado e configurado com sucesso!'
                    : 'Webhook desabilitado com sucesso!',
                variant: 'success',
            });
            setShowWebhookDialog(false);
            setWebhookUrl('');
            setWebhookEnabled(false);
            setPhoneNumberToConfigure(null);
        } catch (error) {
            toast({
                title: 'Erro',
                description: 'Falha ao atualizar configuração do webhook',
                variant: 'destructive',
            });
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'CONNECTED':
                return 'success';
            case 'DISCONNECTED':
                return 'destructive';
            case 'PENDING':
                return 'secondary';
            default:
                return 'default';
        }
    };

    const getChannelIcon = (channel: string) => {
        switch (channel) {
            case 'whatsapp_business_account':
                return <MessageSquare className="w-5 h-5 text-green-600" />;
            case 'facebook':
                return <MessageSquare className="w-5 h-5 text-blue-600" />;
            case 'instagram':
                return <MessageSquare className="w-5 h-5 text-pink-600" />;
            default:
                return <MessageSquare className="w-5 h-5 text-gray-600" />;
        }
    };

    if (error) {
        return (
            <div className="text-center py-8">
                <p className="text-red-600">Erro ao carregar contas: {error.message}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-blue-600">Contas WhatsApp</h1>
                    <p className="text-gray-600">Gerencie suas contas WhatsApp Business</p>
                </div>
                <div className="flex space-x-2">
                    <Button variant="outline" onClick={handleRefresh} disabled={isLoading} className="border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600">
                        <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                        Atualizar
                    </Button>
                    <Button onClick={() => setShowFacebookSignup(true)} className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Conectar Conta
                    </Button>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-500 w-4 h-4" />
                <Input
                    placeholder="Buscar contas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                />
            </div>

            {/* Facebook Signup Modal */}
            <FacebookSignupModal
                open={showFacebookSignup}
                onOpenChange={setShowFacebookSignup}
                onSuccess={handleFacebookSignupSuccess}
            />

            {/* PIN Dialog */}
            <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
                <DialogContent className="sm:max-w-md bg-white">
                    <DialogHeader>
                        <DialogTitle>Registrar Número com PIN</DialogTitle>
                        <DialogDescription>
                            Digite o PIN de 6 dígitos que você deseja usar para registrar este número WhatsApp Business.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label htmlFor="pin">PIN (6 dígitos)</Label>
                            <Input
                                id="pin"
                                type="text"
                                maxLength={6}
                                value={pinValue}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setPinValue(value);
                                }}
                                placeholder="000000"
                                className="mt-2 font-mono text-center text-2xl tracking-widest"
                                autoFocus
                            />
                            <p className="text-xs text-gray-500 mt-2">
                                Digite apenas números (0-9)
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowPinDialog(false);
                                setPinValue('');
                                setAccountToRegister(null);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handlePinSubmit}
                            disabled={registerPhoneNumberMutation.isPending || pinValue.length !== 6}
                        >
                            {registerPhoneNumberMutation.isPending ? 'Registrando...' : 'Registrar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Webhook Configuration Dialog */}
            <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
                <DialogContent className="sm:max-w-lg bg-white">
                    <DialogHeader>
                        <DialogTitle>Configurar Webhook</DialogTitle>
                        <DialogDescription>
                            Configure a URL do webhook para encaminhar mensagens do WhatsApp para seu broker externo.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {phoneNumberToConfigure && (
                            <div className="p-3 bg-blue-50 rounded-md">
                                <p className="text-xs text-blue-800">
                                    <strong>Número:</strong> {phoneNumberToConfigure.displayPhoneNumber}
                                </p>
                                {phoneNumberToConfigure.verifiedName && (
                                    <p className="text-xs text-blue-800 mt-1">
                                        <strong>Nome Verificado:</strong> {phoneNumberToConfigure.verifiedName}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Webhook Toggle */}
                        <div className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex-1">
                                <Label htmlFor="webhookEnabled" className="text-base font-medium cursor-pointer">
                                    Habilitar Webhook
                                </Label>
                                <p className="text-xs text-gray-500 mt-1">
                                    Ative para encaminhar mensagens para seu broker externo
                                </p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    id="webhookEnabled"
                                    checked={webhookEnabled}
                                    onChange={(e) => {
                                        setWebhookEnabled(e.target.checked);
                                        if (!e.target.checked) {
                                            setWebhookUrl('');
                                        }
                                    }}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                        {/* Webhook URL Input - Only shown when enabled */}
                        {webhookEnabled && (
                            <div>
                                <Label htmlFor="webhookUrl">URL do Webhook *</Label>
                                <Input
                                    id="webhookUrl"
                                    type="url"
                                    value={webhookUrl}
                                    onChange={(e) => setWebhookUrl(e.target.value)}
                                    placeholder="https://seu-broker.com/webhook/whatsapp"
                                    className="mt-2"
                                    autoFocus={webhookEnabled}
                                />
                                <p className="text-xs text-gray-500 mt-2">
                                    URL completa do endpoint que receberá as mensagens do WhatsApp
                                </p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowWebhookDialog(false);
                                setWebhookUrl('');
                                setWebhookEnabled(false);
                                setPhoneNumberToConfigure(null);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleWebhookSubmit}
                            disabled={updateWebhookMutation.isPending || (webhookEnabled && !webhookUrl.trim())}
                        >
                            {updateWebhookMutation.isPending ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Account Form */}
            {showCreateForm && (
                <CreateAccountForm
                    onSubmit={handleCreateAccount}
                    onCancel={() => setShowCreateForm(false)}
                    isLoading={createAccountMutation.isPending}
                />
            )}

            {/* Accounts List */}
            {isLoading ? (
                <div className="text-center py-8">Carregando contas...</div>
            ) : filteredAccounts.length === 0 ? (
                <Card className="border-blue-200">
                    <CardContent className="text-center py-8">
                        <MessageSquare className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-blue-600 mb-2">Nenhuma conta encontrada</h3>
                        <p className="text-gray-500 mb-4">
                            {searchTerm ? 'Tente ajustar sua busca' : 'Conecte sua primeira conta WhatsApp Business'}
                        </p>
                        {/* <Button onClick={() => setShowFacebookSignup(true)}>
                            <Plus className="w-4 h-4 mr-2" />
                            Conectar Conta
                        </Button> */}
                    </CardContent>
                </Card>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAccounts.map((account) => (
                        <Card key={account.publicId} className="hover:shadow-md hover:border-blue-300 transition-all border-blue-100">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        {getChannelIcon(account.channel)}
                                        <CardTitle className="text-lg">{account.name}</CardTitle>
                                    </div>
                                    <Badge variant={getStatusColor(account.status) as any}>
                                        {account.status}
                                    </Badge>
                                </div>
                                <CardDescription>
                                    {account.channel} • {account.number || 'Sem número'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 text-sm text-gray-600">
                                    <div className="flex justify-between">
                                        <span>ID:</span>
                                        <span className="font-mono text-xs">{account.publicId}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Criado:</span>
                                        <span>{new Date(account.createdAt).toLocaleDateString()}</span>
                                    </div>
                                    {account.botId && (
                                        <div className="flex justify-between">
                                            <span>Bot:</span>
                                            <span>{account.botId}</span>
                                        </div>
                                    )}
                                </div>
                                {account.channel === 'whatsapp_business_account' && (() => {
                                    const status = getPhoneNumberStatus(account.publicId);
                                    return (
                                        <div className="mt-4 space-y-2">
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-gray-500">Registrado:</span>
                                                <Badge variant={status.isRegistered ? 'success' : 'secondary'} className="text-xs">
                                                    {status.isRegistered ? 'Sim' : 'Não'}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="text-gray-500">Conectado:</span>
                                                <Badge variant={status.isSubscribed ? 'success' : 'secondary'} className="text-xs">
                                                    {status.isSubscribed ? 'Sim' : 'Não'}
                                                </Badge>
                                            </div>
                                            <div className="flex space-x-2 mt-3">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleRegisterPhoneNumber(account.publicId)}
                                                    disabled={registerPhoneNumberMutation.isPending || status.isRegistered}
                                                    className="flex-1 text-xs"
                                                >
                                                    {registerPhoneNumberMutation.isPending ? 'Registrando...' : 'Registrar'}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleSubscribeApp(account.publicId)}
                                                    disabled={subscribeAppMutation.isPending || status.isSubscribed || !status.isRegistered}
                                                    className="flex-1 text-xs"
                                                >
                                                    {subscribeAppMutation.isPending ? 'Conectando...' : 'Conectar'}
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })()}
                                <div className="flex justify-end space-x-2 mt-4">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                        onClick={() => handleOpenWebhookConfig(account)}
                                    >
                                        <Settings className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDeleteAccount(account.publicId)}
                                        disabled={deleteAccountMutation.isPending}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// Create Account Form Component
function CreateAccountForm({
    onSubmit,
    onCancel,
    isLoading
}: {
    onSubmit: (data: CreateWhatsAppRequest) => void;
    onCancel: () => void;
    isLoading: boolean;
}) {
    const [formData, setFormData] = useState<CreateWhatsAppRequest>({
        name: '',
        status: 'CONNECTED',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Criar Nova Conta</CardTitle>
                <CardDescription>
                    Crie uma nova conta WhatsApp Business manualmente
                </CardDescription>
            </CardHeader>
            <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <Label htmlFor="name">Nome da Conta</Label>
                        <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Ex: Minha Empresa WhatsApp"
                            required
                        />
                    </div>

                    <div>
                        <Label htmlFor="botId">Bot ID (Opcional)</Label>
                        <Input
                            id="botId"
                            value={formData.botId || ''}
                            onChange={(e) => setFormData({ ...formData, botId: e.target.value })}
                            placeholder="ID do bot associado"
                        />
                    </div>

                    <div>
                        <Label htmlFor="queueId">Queue ID (Opcional)</Label>
                        <Input
                            id="queueId"
                            value={formData.queueId || ''}
                            onChange={(e) => setFormData({ ...formData, queueId: e.target.value })}
                            placeholder="ID da fila de atendimento"
                        />
                    </div>

                    <div className="flex justify-end space-x-2">
                        <Button type="button" variant="outline" onClick={onCancel}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading ? 'Criando...' : 'Criar Conta'}
                        </Button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
